import { parseTimestamp } from './traderTime';
import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}

export function formatAmount(amount: number | string, currency?: string): string {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return '0.00';

    const formatted = numAmount.toFixed(2);
    return currency ? `${formatted} ${currency}` : formatted;
}

export function formatAmountWithSeparator(amount: number | string, currency?: string, locale: string = 'en-US'): string {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return '0.00';

    const formatted = numAmount.toLocaleString(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
    return currency ? `${formatted} ${currency}` : formatted;
}

/**
 * Утилиты для работы с UTC временем
 */
export const timeUtils = {
    /**
     * Создает Date объект из строки, принудительно интерпретируя как UTC
     */
    parseUTC: (dateString: string): Date => {
        return parseTimestamp(dateString);
    },

    /**
     * Получает текущее время в UTC
     */
    nowUTC: (): Date => {
        return new Date();
    },

    /**
     * Форматирует дату в локальном времени с указанием UTC
     */
    formatDateUTC: (dateString: string, locale: string = 'ru-RU'): string => {
        const utcDate = timeUtils.parseUTC(dateString);
        return utcDate.toLocaleString(locale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'UTC'
        });
    },

    /**
     * Вычисляет разность в миллисекундах между UTC временем и текущим временем
     */
    getTimeDifference: (utcDateString: string): number => {
        const utcDate = timeUtils.parseUTC(utcDateString);
        const now = timeUtils.nowUTC();
        return utcDate.getTime() - now.getTime();
    },

    /**
     * Проверяет, истекло ли время
     */
    isExpired: (utcDateString: string): boolean => {
        return timeUtils.getTimeDifference(utcDateString) <= 0;
    },

    /**
     * Проверяет, истекает ли время в ближайшие минуты
     */
    isExpiringSoon: (utcDateString: string, minutesThreshold: number = 30): boolean => {
        const diffMs = timeUtils.getTimeDifference(utcDateString);
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        return diffMinutes <= minutesThreshold && diffMinutes > 0;
    }
};

export function getExchangeRate(transaction: {
    exchange_rate?: number;
    payer_amount: number;
    payer_currency: string;
    receiver_amount: number;
    receiver_currency: string;
}): number | null {
    if (transaction.exchange_rate !== undefined && transaction.exchange_rate !== null) {
        return transaction.exchange_rate;
    }

    if (!transaction.payer_amount || !transaction.receiver_amount) {
        return null;
    }

    if (transaction.payer_currency !== 'USDT' && transaction.receiver_currency === 'USDT') {
        return transaction.payer_amount / transaction.receiver_amount;
    } else if (transaction.payer_currency === 'USDT' && transaction.receiver_currency !== 'USDT') {
        return transaction.receiver_amount / transaction.payer_amount;
    }

    return null;
}

/** Display full card numbers without changing masked values or account numbers. */
export function formatCardNumber(value: string): string {
    if (!/^[0-9\s]+$/.test(value)) return value;
    const digits = value.replace(/\s/g, '');
    if (digits.length < 13 || digits.length > 19) return value;
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
}
